import React, { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Leaf, Factory, Shield, Printer, CheckCircle, Package, Truck, Map,
  Repeat, ScanLine, Recycle, Archive, Trash2
} from "lucide-react";

const ICONS = {
  "leaf": Leaf, "factory": Factory, "shield": Shield, "printer": Printer,
  "check-circle": CheckCircle, "package": Package, "truck": Truck, "map": Map,
  "repeat": Repeat, "scan-line": ScanLine, "recycle": Recycle, "archive": Archive, "trash-2": Trash2,
};

const PHASE_COLORS = {
  "Upstream": "border-emerald-700 bg-emerald-50",
  "Production": "border-amber-700 bg-amber-50",
  "Distribution": "border-slate-700 bg-slate-50",
  "Use": "border-blue-700 bg-blue-50",
  "End-of-Life": "border-zinc-700 bg-zinc-100",
};

export default function Lifecycle() {
  const [stages, setStages] = useState([]);

  useEffect(() => { api.get("/lifecycle/stages").then((r) => setStages(r.data.stages)); }, []);

  const phases = ["Upstream", "Production", "Distribution", "Use", "End-of-Life"];

  return (
    <div data-testid="lifecycle-page" className="space-y-6">
      <div>
        <div className="overline text-emerald-800">Lifecycle Digital Twin</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Cradle-to-Grave</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">Models all 13 lifecycle stages of a banknote — from raw material extraction to end-of-life polymer recycling.</p>
      </div>

      <div className="space-y-8">
        {phases.map((phase) => {
          const items = stages.filter((s) => s.phase === phase);
          return (
            <div key={phase}>
              <div className="flex items-center gap-3 mb-4">
                <div className="overline text-slate-500">{phase}</div>
                <div className="h-px flex-1 bg-slate-200" />
                <div className="text-xs text-slate-500 font-mono">{items.length} stages</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((s) => {
                  const Icon = ICONS[s.icon] || Leaf;
                  return (
                    <div key={s.id} data-testid={`stage-${s.id}`} className={`border p-5 ${PHASE_COLORS[phase]} hover:-translate-y-1 hover:shadow-lg transition-transform`}>
                      <div className="flex items-start justify-between">
                        <Icon className="w-5 h-5 text-slate-900" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">#{s.id}</span>
                      </div>
                      <div className="font-heading font-bold text-slate-900 mt-3 text-sm leading-snug">{s.stage}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-slate-200 p-6 bg-slate-50 text-sm">
        <div className="overline text-emerald-800 mb-2">Why Lifecycle Matters</div>
        <p className="text-slate-700 leading-relaxed">
          Significant carbon impacts of currency arise <span className="font-semibold">beyond printing</span> — in raw materials, secure logistics, circulation handling, and end-of-life. The Lifecycle Digital Twin allows BSP to attribute and optimize emissions stage-by-stage, supporting the polymer-vs-paper transition and circular economy goals.
        </p>
      </div>
    </div>
  );
}
